import { cmsCDNPath } from 'utils/helpers';

export interface HomepageCmsSpotlight {
  href: string;
  imageSrc?: string;
  spotlight: {
    eyebrowLabel: string;
    series: string;
    name: string;
    desc: string;
    cta: string;
    disclosureLegal?: string;
  };
}

const stripHtml = (value: unknown) => {
  if (typeof value !== 'string') return '';
  return value
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
};

const resolveHomepageHref = (href: unknown) => {
  if (typeof href !== 'string') return undefined;
  const trimmedHref = href.trim();
  if (!trimmedHref || trimmedHref.startsWith('//')) return undefined;
  try {
    const url = new URL(trimmedHref, 'https://www.startengine.com');
    const isStartEngineHost =
      url.hostname === 'startengine.com' || url.hostname.endsWith('.startengine.com');
    if (
      url.protocol !== 'https:' ||
      !isStartEngineHost ||
      url.username ||
      url.password ||
      url.port
    ) {
      return undefined;
    }
    return url.origin === 'https://www.startengine.com'
      ? `${url.pathname}${url.search}${url.hash}`
      : url.toString();
  } catch {
    return undefined;
  }
};

const strapiImageUrl = (image: unknown): string | undefined => {
  if (typeof image === 'string') return image;
  if (!image || typeof image !== 'object') return undefined;
  const record = image as {
    url?: unknown;
    hash?: unknown;
    ext?: unknown;
    data?: { attributes?: { url?: unknown; hash?: unknown; ext?: unknown } };
  };
  const attrs = record.data?.attributes;
  const url = attrs?.url ?? record.url;
  if (typeof url === 'string' && url.trim()) return url;
  const hash = attrs?.hash ?? record.hash;
  const ext = attrs?.ext ?? record.ext;
  return typeof hash === 'string' && hash.trim() && typeof ext === 'string' && ext.trim()
    ? `${cmsCDNPath}/images/origin/${hash}${ext}`
    : undefined;
};

/**
 * Convert the currently published `homepages` entry into the shared editorial
 * spotlight model. Homepage and Explore must both use this function so a
 * scheduled CMS feature cannot diverge between routes.
 */
export const buildHomepageCmsSpotlight = (
  attributes: Record<string, unknown> | undefined,
  isMobile: boolean,
): HomepageCmsSpotlight | null => {
  const title =
    stripHtml(isMobile ? attributes?.hero_title_mobile : undefined) ||
    stripHtml(attributes?.hero_title_desktop);
  const mobileHref = isMobile
    ? resolveHomepageHref(attributes?.hero_button_link_mobile)
    : undefined;
  const href = mobileHref || resolveHomepageHref(attributes?.hero_button_link_desktop);
  if (!title || !href) return null;
  const imageSrc =
    (isMobile ? strapiImageUrl(attributes?.hero_image_mobile) : undefined) ??
    strapiImageUrl(attributes?.hero_image);
  const legal = stripHtml(attributes?.hero_discloser);
  return {
    href,
    ...(imageSrc ? { imageSrc } : {}),
    spotlight: {
      eyebrowLabel: 'Featured Offering',
      series: '',
      name: title,
      desc:
        stripHtml(isMobile ? attributes?.hero_subheading_mobile : undefined) ||
        stripHtml(attributes?.hero_subheading_desktop) ||
        `Invest in ${title} on StartEngine.`,
      cta:
        stripHtml(isMobile ? attributes?.hero_button_copy_mobile : undefined) ||
        stripHtml(attributes?.hero_button_copy_desktop) ||
        stripHtml(attributes?.hero_button_text_desktop) ||
        'View opportunity',
      ...(legal ? { disclosureLegal: legal } : {}),
    },
  };
};
